import { useCallback, useState, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingButton, Avatar } from '@/components';
import { ReactComponent as PlusIcon } from '@/assets/icons/icon-plus-lg.svg';
import { ReactComponent as CheckIcon } from '@/assets/icons/icon-check-lg.svg';
import { Button } from 'react-bootstrap';
import { AdvisorModel } from '@/lib/models';
import { advisorService } from '@/lib/services';
import { useHandleError } from '@/hooks';

interface AuthorsListProps {
	authors?: AdvisorModel[];
	onAfterFollowToggle?: () => void;
	className?: string;
	showViewBioButton?: boolean;
	showOnlyList?: boolean;
}

export const AuthorsList: FC<AuthorsListProps> = ({ authors = [], onAfterFollowToggle, className, showViewBioButton = false, showOnlyList = false }) => {
	const navigate = useNavigate();
	const handleError = useHandleError();
	const [authorLoadingById, setAuthorLoadingById] = useState<Record<string, boolean>>({});

	const handleFollowButtonClick = useCallback(
		async (authorId: string, follow: boolean) => {
			setAuthorLoadingById(prev => ({ ...prev, [authorId]: true }));

			try {
				if (!authorId) {
					throw new Error('Author is undefined.');
				}

				await advisorService.followAuthor({ authorId: authorId, follow }).fetch();
				await onAfterFollowToggle?.();
			} catch (error) {
				handleError(error);
			} finally {
				setAuthorLoadingById(prev => ({ ...prev, [authorId]: false }));
			}
		},
		[handleError, onAfterFollowToggle]
	);

	if (!authors || authors.length === 0) return null;

	return (
		<div className={className}>
			{!showOnlyList ? (
				<>
					<hr className="mb-5" />
					<h3 className="mb-1">Authors</h3>
				</>
			) : null}
			<div>
				<ul className="list-unstyled">
					{authors
						.map((author, index, filteredAuthors) => (
							<li
								key={author.advisorId}
								className="d-flex align-items-center justify-content-between"
								style={{
									height: '80px',
									borderBottom: index < filteredAuthors.length - 1 ? '1px solid #ccc' : 'none',
								}}
							>
								<div className="mb-2 mt-2 d-flex align-items-center">
									<Avatar className="mr-3" size={48} url={author.imageUrl} />
									<div>
										<h4 className="mb-0">{author.name}</h4>
										<h5 className="mb-0 font-weight-regular text-gray-700">
											{author.position}
										</h5>
									</div>
								</div>
								<div className="d-flex align-items-center gap-3">
									{author?.userIsFollowing ? (
										<LoadingButton
											isLoading={!!authorLoadingById[author.advisorId]}
											classNameInner="d-flex align-items-center gap-2"
											style={{ width: "150px" }}
											variant="outline-primary"
											onClick={() => handleFollowButtonClick(author.advisorId, false)}
										>
											<CheckIcon className="" width={20} height={20} />
											<span>Following</span>
										</LoadingButton>
									) : (
										<LoadingButton
											isLoading={!!authorLoadingById[author.advisorId]}
											classNameInner="d-flex align-items-center gap-2"
											style={{ width: "150px" }}
											variant="primary"
											onClick={() => handleFollowButtonClick(author.advisorId, true)}
										>
											<PlusIcon className="" width={20} height={20} />
											<span>Follow</span>
										</LoadingButton>
									)}
									{showViewBioButton ? (
										<Button
											variant="outline-primary"
											onClick={() => {
												navigate(`/advisory-team/${author.advisorId}`);
											}}
										>
											<span>View Bio</span>
										</Button>
									) : null}
								</div>
							</li>
						))}
				</ul>
			</div>
		</div>
	);
};
